import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import {
  getNumberMaskedValue,
  useNumberMask
} from "@elliemae/ds-form-helpers-mask-hooks";
import { DSInputText } from "@elliemae/ds-form-input-text";
import { DSFormLayoutBlockItem } from "@elliemae/ds-form-layout-blocks";
import { Grid } from "@elliemae/ds-grid";
import { DSHeader } from "@elliemae/ds-header";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Controller,
  useWatch
} from "react-hook-form";
import { DSControlledCheckbox } from "@elliemae/ds-controlled-form";
import { AdjustFeeDialog } from "./adjustFeeDialog";
import { calculateGovtFeeAmount } from "../utils/helpers";
import {
  useOriginalLoanInformationSelector,
  getUpfrontPPEFeeValue,
  isGovtLoan,
  useLookupOptions,
  useLoanDataFieldSelector,
  getEncFeeFactor
} from "../utils/utils";
import { EppsLoanInformation } from "../../../../models/epps-models";
import { DSFormLayoutBlockItemWithValidation } from "../../../ds-form-components";
const PERCENT = {
  allowNegative: true,
  decimalPlaces: 6,
  suffix: "%",
  includeThousandsSeparator: false
};
const CURRENCY = {
  allowNegative: false,
  decimalPlaces: 2,
  prefix: "$"
};
const userSelectedFactorMap = /* @__PURE__ */ new WeakMap();
const userSelectedUsdaFactorMap = /* @__PURE__ */ new WeakMap();
const GovernmentFees = ({
  schemaForm,
  fields,
  rest,
  preview
}) => {
  const getFieldByComponentId = (componentId) => {
    return fields.find((field) => field.component_id === componentId);
  };
  const usdaGuarantee = useWatch({
    control: schemaForm.control,
    name: "loanInformation.usdaGuarantee"
  });
  const standardProducts = useWatch({
    control: schemaForm.control,
    name: "standardProducts"
  });
  const specialProducts = useWatch({
    control: schemaForm.control,
    name: "specialProducts"
  });
  const ltv = useWatch({
    control: schemaForm.control,
    name: "loanInformation.ltv"
  });
  const loanPurpose = useWatch({
    control: schemaForm.control,
    name: "loanInformation.loanPurpose"
  });
  const formFeeFactor = useWatch({
    control: schemaForm.control,
    name: "loanInformation.govtFeeFactor"
  });
  const watchFirstMortgageAmount = useWatch({
    control: schemaForm.control,
    name: "loanInformation.firstMortgageAmount"
  });
  const watchSecondMortgageAmount = useWatch({
    control: schemaForm.control,
    name: "loanInformation.secondMortgageAmount"
  });
  const watchVaFundingFeeExempted = useWatch({
    control: schemaForm.control,
    name: "loanInformation.vaFundingFeeExempted"
  });
  const watchVaFirstTimeUse = useWatch({
    control: schemaForm.control,
    name: "loanInformation.vaFirstTimeUse"
  });
  const {
    loanInformation: {
      firstMortgageAmount,
      secondMortgageAmount,
      lienPosition,
      vaFirstTimeUse,
      govtFeeFactor,
      vaFundingFeeExempted,
      feeAmount,
      feeAmountFinanced,
      feeAmountPaidinCash,
      scenario,
      feeFactor
    }
  } = schemaForm.getValues();
  const isUSDA = useMemo(
    () => standardProducts?.includes(EppsLoanInformation.StandardProducts.USDA),
    [standardProducts]
  );
  const govtUpfrontFees = useLookupOptions("fee");
  const originalLoanInformation = useOriginalLoanInformationSelector();
  const sectionOfAct = useLoanDataFieldSelector("loanInformation.sectionOfAct");
  const usdaGuaranteeEncVal = useLoanDataFieldSelector(
    "loanInformation.usdaGuarantee"
  );
  const [checkedFactor, setCheckedFactor] = useState("");
  const initialValuesLoadedRef = useRef(false);
  const lastCalculationParamsRef = useRef("");
  const prevHadGovtProductRef = useRef(false);
  const savedFinancedAmountRef = useRef(null);
  const usdaEncFactorRef = useRef(null);
  const usdaPPEFactorRef = useRef(null);
  const factorField = getFieldByComponentId("Factor");
  const amountField = getFieldByComponentId("Amount");
  const financedAmountField = getFieldByComponentId("Financed Amount");
  const paidInCashField = getFieldByComponentId("Paid in Cash");
  const vaExemptField = getFieldByComponentId("VA exempt");
  const vaFirstTimeUseField = getFieldByComponentId("1st Time use of VA loan");
  const [showGovernmentFees, setShowGovernmentFees] = useState(
    [
      EppsLoanInformation.StandardProducts.FHA,
      EppsLoanInformation.StandardProducts.VA,
      EppsLoanInformation.StandardProducts.USDA,
      EppsLoanInformation.SpecialProducts.HUD
    ].some(
      (product) => [...standardProducts ?? [], ...specialProducts ?? []]?.includes(
        product
      )
    )
  );
  const [paidInCashDisabled, setPaidInCashDisabled] = useState(isUSDA);
  const [headerLabel, setHeaderLabel] = useState("Government Fees");
  const [exempt, setExempt] = useState(false);
  const [factorValue, setFactorValue] = useState("");
  const [amountValue, setAmountValue] = useState("");
  const [financedAmountValue, setFinancedAmountValue] = useState("");
  const [paidInCashValue, setPaidInCashValue] = useState("");
  const [open, setOpen] = useState(false);
  const [showAdjustLink, setShowAdjustLink] = useState(false);
  const closeDialog = useCallback(() => {
    setOpen(false);
  }, []);
  const openDialog = useCallback(() => {
    setOpen(true);
  }, []);
  const useMaskedInputProps = (setter, maskType) => {
    return useNumberMask({ valueSetter: setter, ...maskType });
  };
  const factorInputProps = useMaskedInputProps(setFactorValue, PERCENT);
  const amountInputProps = useMaskedInputProps(setAmountValue, CURRENCY);
  const financedAmountInputProps = useMaskedInputProps(
    setFinancedAmountValue,
    CURRENCY
  );
  const paidInCashInputProps = useNumberMask({
    valueSetter: setPaidInCashValue,
    ...CURRENCY
  });
  const formatCurrencyTwoDecimals = (raw) => {
    const num = typeof raw === "number" ? raw : Number(raw.replace(/[^0-9.-]/g, ""));
    if (!Number.isFinite(num)) return String(raw);
    const [intPart, decPart] = num.toFixed(2).split(".");
    const intWithSep = Number(intPart).toLocaleString("en-US");
    return `$${intWithSep}.${decPart}`;
  };
  const useMaskedFieldEffect = (field, maskType, valueSetter) => {
    useEffect(() => {
      const modelPath = field?.encompass_ids?.[0]?.modelPath;
      const value = schemaForm.getValues(modelPath);
      const normalizedValue = typeof value !== "number" || value % 1 === 0 || modelPath === "loanInformation.govtFeeFactor" ? value : value.toFixed(2);
      const maskedValue = getNumberMaskedValue(
        String(normalizedValue ?? ""),
        maskType
      );
      valueSetter(maskedValue);
    }, [schemaForm.watch(field?.encompass_ids?.[0]?.modelPath)]);
  };
  useMaskedFieldEffect(factorField, PERCENT, setFactorValue);
  useMaskedFieldEffect(amountField, CURRENCY, setAmountValue);
  useMaskedFieldEffect(financedAmountField, CURRENCY, setFinancedAmountValue);
  useMaskedFieldEffect(paidInCashField, CURRENCY, setPaidInCashValue);
  const updateGovtField = (path, value) => {
    schemaForm.setValue(`loanInformation.${path}`, value);
    switch (path) {
      case "feeAmountFinanced":
        setFinancedAmountValue(
          getNumberMaskedValue(value.toString(), CURRENCY)
        );
        break;
      case "feeAmount":
        setAmountValue(formatCurrencyTwoDecimals(value));
        break;
      case "feeAmountPaidinCash":
        setPaidInCashValue(formatCurrencyTwoDecimals(value));
        break;
      case "financedAmount":
      case "feeFactor":
      case "feePercent":
      case "normalizedFeeFactor":
      case "usdaGuarantee":
        break;
      case "govtFeeFactor":
        setFactorValue(getNumberMaskedValue(value.toString(), PERCENT));
        break;
      default:
        break;
    }
  };
  const calculateGovtFeeFinancedAndPaidInCash = (stringVal, path) => {
    let newFeeAmountFinanced = 0;
    let newPaidInCash = 0;
    const truncateToCurrency = (val) => Number(Number(val).toFixed(2));
    const newValNumber = Number(stringVal.replace(/[^0-9.-]/g, ""));
    const oldValue = path === "feeAmountFinanced" ? feeAmountFinanced : feeAmountPaidinCash;
    if (oldValue === newValNumber) {
      return;
    }
    const maxAmount = truncateToCurrency(feeAmount);
    const centsFromFinanced = Number(Number(maxAmount % 1).toFixed(2));
    const currentFeeAmountFinanced = truncateToCurrency(feeAmountFinanced);
    const currentFeeAmountPaidInCash = truncateToCurrency(feeAmountPaidinCash);
    if (path === "feeAmountFinanced") {
      newFeeAmountFinanced = truncateToCurrency(Math.floor(newValNumber));
      newPaidInCash = truncateToCurrency(
        Math.max(feeAmount - newFeeAmountFinanced, 0)
      );
      if (isUSDA) {
        const baseLoanAmountNum = lienPosition === 1 ? firstMortgageAmount : secondMortgageAmount;
        if (typeof baseLoanAmountNum === "number" && baseLoanAmountNum > 0) {
          const updatedFactor = Number(
            ((baseLoanAmountNum + newFeeAmountFinanced) / baseLoanAmountNum).toFixed(6)
          );
          schemaForm.setValue("loanInformation.scenario", "PARTIALLY_FINANCED");
          updateGovtField("govtFeeFactor", updatedFactor);
        }
      }
    } else {
      newPaidInCash = truncateToCurrency(
        Math.floor(newValNumber) + centsFromFinanced
      );
      newFeeAmountFinanced = truncateToCurrency(
        Math.floor(Math.max(feeAmount - newPaidInCash, 0))
      );
    }
    if (currentFeeAmountPaidInCash !== newPaidInCash) {
      updateGovtField("feeAmountPaidinCash", newPaidInCash);
    }
    if (currentFeeAmountFinanced !== newFeeAmountFinanced) {
      updateGovtField("feeAmountFinanced", newFeeAmountFinanced);
      updateGovtField("financedAmount", newFeeAmountFinanced);
    }
  };
  const calculateAndUpdateUsdaFees = useCallback(
    async (feeFactorValue, baseLoanAmountValue, financedAmountValue2) => {
      const usdaResult = await rest.calculateGovie({
        baseLoanAmount: baseLoanAmountValue,
        feeAmountFinanced: financedAmountValue2,
        feeFactor: feeFactorValue,
        productType: "usda",
        systemFeeFactor: 1,
        sourceSystem: "",
        loanGuid: "",
        sourceRefId: ""
      });
      if (usdaResult) {
        updateGovtField("feeAmount", usdaResult.totalFeeAmount);
        updateGovtField("financedAmount", usdaResult.feeAmountFinanced);
        updateGovtField("feeAmountFinanced", usdaResult.feeAmountFinanced);
        updateGovtField("feeAmountPaidinCash", usdaResult.feeAmountPaidInCash);
        updateGovtField("govtFeeFactor", usdaResult.feePercent);
        updateGovtField("feeFactor", Number(feeFactorValue));
        updateGovtField("feePercent", usdaResult.feePercent);
        updateGovtField("normalizedFeeFactor", usdaResult.normalizedFeeFactor);
        updateGovtField("usdaGuarantee", Number(feeFactorValue));
        userSelectedUsdaFactorMap.set(schemaForm, Number(feeFactorValue));
        setCheckedFactor(String(feeFactorValue));
        if (feeFactorValue === 1) {
          usdaPPEFactorRef.current = usdaResult.feePercent;
        } else {
          usdaEncFactorRef.current = usdaResult.feePercent;
        }
      }
      return usdaResult;
    },
    [rest]
  );
  const isOnlyVaProduct = useMemo(() => {
    return isGovtLoan(standardProducts, specialProducts) && !standardProducts?.includes(EppsLoanInformation.StandardProducts.USDA) && !standardProducts?.includes(EppsLoanInformation.StandardProducts.FHA) && standardProducts?.includes(EppsLoanInformation.StandardProducts.VA);
  }, [standardProducts, specialProducts]);
  const handleVaExemptChange = (newVal, renderedField) => {
    renderedField.onChange(newVal);
    setExempt(newVal);
    schemaForm.setValue("loanInformation.govtFeeFactor", 0);
  };
  const handleVaFirstTimeUseChange = (newVal, renderedField) => {
    renderedField.onChange(newVal);
  };
  useEffect(() => {
    const isVAProduct = standardProducts?.includes(
      EppsLoanInformation.StandardProducts.VA
    );
    if (!isVAProduct) {
      schemaForm.setValue("loanInformation.vaFundingFeeExempted", null);
      schemaForm.setValue("loanInformation.vaFirstTimeUse", null);
    }
  }, [standardProducts, vaFundingFeeExempted, schemaForm, vaFirstTimeUse]);
  useEffect(() => {
    const hasGovtProduct = Boolean(isGovtLoan(standardProducts, specialProducts));
    if (hasGovtProduct && !prevHadGovtProductRef.current) {
      savedFinancedAmountRef.current = schemaForm.getValues("loanInformation.financedAmount") ?? null;
    } else if (!hasGovtProduct && prevHadGovtProductRef.current) {
      schemaForm.setValue(
        "loanInformation.financedAmount",
        savedFinancedAmountRef.current ?? 0
      );
      savedFinancedAmountRef.current = null;
    }
    prevHadGovtProductRef.current = hasGovtProduct;
    const updateFeeAmount = async () => {
      if (formFeeFactor >= 0) {
        if (initialValuesLoadedRef.current) {
          let calculatedFeeAmount = (
            // support Conventional loan fees
            schemaForm.getValues("loanInformation.financedAmount") || 0
          );
          if (vaFundingFeeExempted && standardProducts?.includes(EppsLoanInformation.StandardProducts.VA)) {
            calculatedFeeAmount = 0;
          } else if (isGovtLoan(standardProducts, specialProducts)) {
            if (isUSDA) {
              const baseLoanAmountValue = lienPosition === 1 ? firstMortgageAmount : secondMortgageAmount;
              const feeFactorValue = schemaForm.getValues("loanInformation.feeFactor") || schemaForm.getValues("loanInformation.usdaGuarantee") || 1;
              const financedAmountValue2 = scenario === "FULLY_FINANCED" ? null : feeAmountFinanced || 0;
              const calculationParams = JSON.stringify({
                baseLoanAmountValue,
                feeFactorValue,
                financedAmountValue: financedAmountValue2
              });
              if (lastCalculationParamsRef.current === calculationParams) {
                return;
              }
              lastCalculationParamsRef.current = calculationParams;
              await calculateAndUpdateUsdaFees(
                feeFactorValue,
                baseLoanAmountValue,
                financedAmountValue2
              );
              initialValuesLoadedRef.current = true;
              return;
            }
            const baseLoanAmount = lienPosition === 1 ? firstMortgageAmount : secondMortgageAmount;
            calculatedFeeAmount = calculateGovtFeeAmount(
              baseLoanAmount,
              govtFeeFactor,
              standardProducts
            );
          }
          const currentFeeAmount = schemaForm.getValues(
            "loanInformation.feeAmount"
          );
          const currentFeeAmountFinanced = schemaForm.getValues(
            "loanInformation.feeAmountFinanced"
          );
          const currentFeeAmountPaidInCash = schemaForm.getValues(
            "loanInformation.feeAmountPaidinCash"
          );
          const hasExistingSplit = currentFeeAmountFinanced != null && currentFeeAmountPaidInCash != null && Number(currentFeeAmountFinanced) + Number(currentFeeAmountPaidInCash) > 0;
          const feeAmountChanged = currentFeeAmount !== calculatedFeeAmount;
          const centsFinanced = Number(
            Number(calculatedFeeAmount % 1).toFixed(2)
          );
          if (!isGovtLoan(standardProducts, specialProducts)) {
            updateGovtField("feeAmount", calculatedFeeAmount);
          }
          const isVaExempt = vaFundingFeeExempted && standardProducts?.includes(EppsLoanInformation.StandardProducts.VA);
          const hasValidGovtCalc = formFeeFactor > 0 || isVaExempt;
          const shouldUpdate = !isGovtLoan(standardProducts, specialProducts) || hasValidGovtCalc;
          if ((feeAmountChanged || !hasExistingSplit) && formFeeFactor !== null && shouldUpdate) {
            updateGovtField("feeAmount", calculatedFeeAmount);
            updateGovtField("financedAmount", Math.floor(calculatedFeeAmount));
            updateGovtField(
              "feeAmountFinanced",
              Math.floor(calculatedFeeAmount)
            );
            updateGovtField("feeAmountPaidinCash", centsFinanced);
          }
        }
        initialValuesLoadedRef.current = true;
      }
    };
    updateFeeAmount();
  }, [
    formFeeFactor,
    watchFirstMortgageAmount,
    watchSecondMortgageAmount,
    watchVaFundingFeeExempted,
    watchVaFirstTimeUse,
    standardProducts
  ]);
  useEffect(() => {
    const feeConfig = {
      2: {
        name: "FHA",
        label: "UFMIP Fee",
        paidInCashDisabled: false,
        exempt: false
      },
      3: {
        name: "VA",
        label: "Funding Fee",
        paidInCashDisabled: false,
        exempt: vaFundingFeeExempted
      },
      4: {
        name: "USDA",
        label: "Guarantee Fee",
        paidInCashDisabled: true,
        exempt: false
      },
      136: {
        name: "",
        label: "Fee",
        paidInCashDisabled: false,
        exempt: false
      }
    };
    const govtProduct = [
      EppsLoanInformation.StandardProducts.FHA,
      EppsLoanInformation.StandardProducts.VA,
      EppsLoanInformation.StandardProducts.USDA,
      EppsLoanInformation.SpecialProducts.HUD
    ].find(
      (product) => standardProducts?.includes(product) || specialProducts?.includes(product)
    );
    setShowGovernmentFees(Boolean(govtProduct));
    if (!isUSDA) {
      schemaForm.setValue("loanInformation.scenario", "FULLY_FINANCED");
      lastCalculationParamsRef.current = "";
    }
    if (govtProduct) {
      const config = feeConfig[govtProduct];
      setHeaderLabel(config.label);
      setPaidInCashDisabled(config.paidInCashDisabled);
      setExempt(config.exempt);
    }
  }, [standardProducts, specialProducts]);
  const encFeeFactor = useMemo(() => {
    if (standardProducts?.includes(EppsLoanInformation.StandardProducts.FHA) || standardProducts?.includes(EppsLoanInformation.StandardProducts.VA) || specialProducts?.includes(EppsLoanInformation.SpecialProducts.HUD)) {
      const ppeGovtFeeFactor = getUpfrontPPEFeeValue(
        { standardProducts, specialProducts },
        { govtUpfrontFees, loanPurpose, vaFirstTimeUse, ltv }
      );
      return getEncFeeFactor(originalLoanInformation, ppeGovtFeeFactor);
    }
    return null;
  }, [standardProducts, specialProducts]);
  const ppeFeeFactor = useMemo(() => {
    if (exempt) {
      return 0;
    }
    const hasProducts = Boolean(
      standardProducts?.length || specialProducts?.length
    );
    if (hasProducts && ltv) {
      const val = getUpfrontPPEFeeValue(
        { standardProducts, specialProducts },
        { govtUpfrontFees, loanPurpose, vaFirstTimeUse, ltv }
      );
      return val;
    }
    return null;
  }, [
    standardProducts,
    specialProducts,
    loanPurpose,
    vaFirstTimeUse,
    ltv,
    exempt
  ]);
  useEffect(() => {
    const fieldPath = factorField?.encompass_ids?.[0]?.modelPath;
    const effectiveEncFeeFactor = isUSDA ? usdaEncFactorRef.current : encFeeFactor;
    const effectivePpeFeeFactor = isUSDA ? usdaPPEFactorRef.current : ppeFeeFactor;
    const factorsDefined = effectiveEncFeeFactor != null && effectivePpeFeeFactor != null && effectiveEncFeeFactor !== 0;
    const mortgageType = originalLoanInformation?.mortgageType;
    const productMap = {
      FHA: 2,
      VA: 3,
      FarmersHomeAdministration: 4
    };
    const sameProduct = mortgageType && standardProducts?.includes(productMap[mortgageType]) || sectionOfAct === "184" && specialProducts?.includes(136) || false;
    const userSelectedFactor = isUSDA ? userSelectedUsdaFactorMap.get(schemaForm) : userSelectedFactorMap.get(schemaForm);
    const feeFactorValue = userSelectedFactor !== void 0 && (userSelectedFactor === effectiveEncFeeFactor && sameProduct || userSelectedFactor === effectivePpeFeeFactor) ? userSelectedFactor : sameProduct && effectiveEncFeeFactor && effectiveEncFeeFactor !== 0 ? effectiveEncFeeFactor : effectivePpeFeeFactor;
    const showAdjustLinkUSDA = isUSDA && usdaGuaranteeEncVal != null && usdaGuaranteeEncVal !== 1;
    setShowAdjustLink(
      (factorsDefined && effectiveEncFeeFactor !== effectivePpeFeeFactor || showAdjustLinkUSDA) && sameProduct
    );
    schemaForm.setValue(fieldPath, feeFactorValue);
    setFactorValue(`${feeFactorValue}%`);
  }, [encFeeFactor, ppeFeeFactor, isUSDA]);
  if (!showGovernmentFees) {
    return null;
  }
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Grid, { span: 12, children: /* @__PURE__ */ jsx(
      DSHeader,
      {
        text: headerLabel,
        fontSize: 12,
        fontWeight: "bold",
        lineHeight: 1,
        style: { marginTop: "16px" },
        color: void 0,
        fontFamily: void 0,
        height: void 0,
        level: void 0,
        width: void 0
      }
    ) }),
    /* @__PURE__ */ jsx(
      Controller,
      {
        name: factorField?.encompass_ids?.[0]?.modelPath,
        render: ({ field: renderedField }) => /* @__PURE__ */ jsx(Grid, { span: factorField?.ui.col_size, children: /* @__PURE__ */ jsxs(
          DSFormLayoutBlockItem,
          {
            name: factorField?.component_id,
            label: factorField?.label,
            required: factorField?.required,
            feedbackMessage: exempt ? "Exempt" : "",
            children: [
              /* @__PURE__ */ jsx(
                DSInputText,
                {
                  ...renderedField,
                  ...factorInputProps,
                  value: factorValue,
                  onChange: (e) => {
                    factorInputProps.onChange(
                      e
                    );
                  },
                  disabled: true,
                  id: factorField?.encompass_ids?.[0]?.modelPath
                }
              ),
              !exempt && showAdjustLink && /* @__PURE__ */ jsx(
                "div",
                {
                  style: {
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: "1px"
                  },
                  children: /* @__PURE__ */ jsx(
                    "span",
                    {
                      style: {
                        fontSize: "10px",
                        lineHeight: "16px",
                        color: "#0070d2",
                        cursor: "pointer",
                        display: "inline-block"
                      },
                      onClick: openDialog,
                      children: "Adjust fee factor"
                    }
                  )
                }
              )
            ]
          }
        ) })
      }
    ),
    /* @__PURE__ */ jsx(
      Controller,
      {
        name: amountField?.encompass_ids?.[0]?.modelPath,
        render: ({ field: renderedField }) => /* @__PURE__ */ jsx(
          Grid,
          {
            span: amountField?.ui.col_size,
            style: { display: exempt ? "none" : "block" },
            children: /* @__PURE__ */ jsx(
              DSFormLayoutBlockItem,
              {
                name: amountField?.component_id,
                label: amountField?.label,
                required: amountField?.required,
                children: /* @__PURE__ */ jsx(
                  DSInputText,
                  {
                    ...renderedField,
                    ...amountInputProps,
                    value: amountValue,
                    onChange: (e) => {
                      amountInputProps.onChange(
                        e
                      );
                    },
                    disabled: true,
                    id: amountField?.encompass_ids?.[0]?.modelPath
                  }
                )
              }
            )
          }
        )
      }
    ),
    /* @__PURE__ */ jsx(
      Controller,
      {
        name: financedAmountField?.encompass_ids?.[0]?.modelPath,
        render: ({ field: renderedField, fieldState }) => /* @__PURE__ */ jsx(
          Grid,
          {
            span: financedAmountField?.ui.col_size,
            style: { display: exempt ? "none" : "block" },
            children: /* @__PURE__ */ jsx(
              DSFormLayoutBlockItemWithValidation,
              {
                name: financedAmountField?.component_id,
                label: financedAmountField?.label,
                required: financedAmountField?.required,
                fieldState,
                children: /* @__PURE__ */ jsx(
                  DSInputText,
                  {
                    ...renderedField,
                    ...financedAmountInputProps,
                    value: financedAmountValue,
                    onChange: (e) => {
                      financedAmountInputProps.onChange(
                        e
                      );
                    },
                    onBlur: (event) => {
                      calculateGovtFeeFinancedAndPaidInCash(
                        event.target.value,
                        "feeAmountFinanced"
                      );
                    },
                    id: financedAmountField?.encompass_ids?.[0]?.modelPath
                  }
                )
              }
            )
          }
        )
      }
    ),
    /* @__PURE__ */ jsx(
      Controller,
      {
        name: paidInCashField?.encompass_ids?.[0]?.modelPath,
        render: ({ field: renderedField, fieldState }) => /* @__PURE__ */ jsx(
          Grid,
          {
            span: paidInCashField?.ui.col_size,
            style: { display: exempt ? "none" : "block" },
            children: /* @__PURE__ */ jsx(
              DSFormLayoutBlockItemWithValidation,
              {
                name: paidInCashField?.component_id,
                label: paidInCashField?.label,
                required: paidInCashField?.required,
                fieldState,
                children: /* @__PURE__ */ jsx(
                  DSInputText,
                  {
                    ...renderedField,
                    ...paidInCashInputProps,
                    value: paidInCashValue,
                    onChange: (e) => {
                      paidInCashInputProps.onChange(
                        e
                      );
                    },
                    onBlur: (event) => {
                      calculateGovtFeeFinancedAndPaidInCash(
                        event.target.value,
                        "feeAmountPaidInCash"
                      );
                    },
                    disabled: paidInCashDisabled,
                    id: paidInCashField?.encompass_ids?.[0]?.modelPath
                  }
                )
              }
            )
          }
        )
      }
    ),
    /* @__PURE__ */ jsx(
      AdjustFeeDialog,
      {
        open,
        onClose: closeDialog,
        encFeeFactor: isUSDA ? usdaGuaranteeEncVal : encFeeFactor,
        ppeFeeFactor,
        checkedFactor,
        setCheckedFactor,
        factorValue: Number(factorValue.replace(/[^0-9.-]/g, "")),
        onApply: async () => {
          if (Number(factorValue.replace(/[^d]+/g, "")) !== Number(checkedFactor)) {
            schemaForm.setValue(
              factorField?.encompass_ids?.[0]?.modelPath,
              Number(checkedFactor)
            );
            setFactorValue(checkedFactor);
            if (isUSDA) {
              const baseLoanAmountValue = lienPosition === 1 ? firstMortgageAmount : secondMortgageAmount;
              const financedAmountValue2 = scenario === "FULLY_FINANCED" ? null : feeAmountFinanced || 0;
              await calculateAndUpdateUsdaFees(
                Number(checkedFactor),
                baseLoanAmountValue,
                financedAmountValue2
              );
            }
            userSelectedFactorMap.set(schemaForm, Number(checkedFactor));
          }
          closeDialog();
        }
      }
    ),
    isOnlyVaProduct && /* @__PURE__ */ jsxs(Fragment, { children: [
      vaExemptField && /* @__PURE__ */ jsx(
        Controller,
        {
          name: vaExemptField?.encompass_ids?.[0]?.modelPath,
          render: ({ field: renderedField, fieldState }) => /* @__PURE__ */ jsx(Grid, { span: vaExemptField?.ui.col_size, children: /* @__PURE__ */ jsx(
            DSFormLayoutBlockItemWithValidation,
            {
              name: vaExemptField?.component_id,
              label: vaExemptField?.label,
              required: vaExemptField?.required,
              fieldState,
              children: /* @__PURE__ */ jsx(
                DSControlledCheckbox,
                {
                  ...renderedField,
                  checked: renderedField?.value ?? false,
                  id: vaExemptField?.component_id,
                  onChange: (newVal, e) => {
                    handleVaExemptChange(newVal, renderedField);
                  }
                }
              )
            }
          ) })
        }
      ),
      vaFirstTimeUseField && /* @__PURE__ */ jsx(
        Controller,
        {
          name: vaFirstTimeUseField?.encompass_ids?.[0]?.modelPath,
          render: ({ field: renderedField, fieldState }) => /* @__PURE__ */ jsx(Grid, { span: vaFirstTimeUseField?.ui.col_size, children: /* @__PURE__ */ jsx(
            DSFormLayoutBlockItemWithValidation,
            {
              name: vaFirstTimeUseField?.component_id,
              label: vaFirstTimeUseField?.label,
              required: vaFirstTimeUseField?.required,
              fieldState,
              children: /* @__PURE__ */ jsx(
                DSControlledCheckbox,
                {
                  ...renderedField,
                  checked: renderedField?.value ?? false,
                  id: vaFirstTimeUseField?.component_id,
                  onChange: (newVal, e) => {
                    handleVaFirstTimeUseChange(newVal, renderedField);
                  }
                }
              )
            }
          ) })
        }
      )
    ] })
  ] });
};
export {
  GovernmentFees
};
